import { QueryClient } from '@tanstack/react-query';
import { CALIBRATION_TASK_ROOT_API_URL } from '../constants';
import { Params } from '@/utils/types';
import {
	CalibrationTasksSortableColumns,
	CalibrationTaskListResponse,
	QueryCalibrationTasksListArgs,
	CalibrationsTaskStatus
} from './types';
import { buildQueryParams } from '@/utils/build-query-params';
import { TaskType } from '../types';
import apiClient from '@/api';

export const DEFAULT_PAGE_SIZE = '10';

export const taskTypeLabels: Record<TaskType, string> = {
	[TaskType.GET_DATA]: 'Download calibration',
	[TaskType.CROSS_VALIDATION]: 'Cross validation',
	[TaskType.UNIT_VECTOR]: 'Unit vector',
	[TaskType.REVISION_DATA]: 'Revision data'
};

export const taskStatusLabels: Record<CalibrationsTaskStatus, string> = {
	[CalibrationsTaskStatus.SUCCESS]: 'Success',
	[CalibrationsTaskStatus.STARTED]: 'Running',
	[CalibrationsTaskStatus.REQUESTED]: 'Requested',
	[CalibrationsTaskStatus.REQUEST_FAILED]: 'Request failed',
	[CalibrationsTaskStatus.REQUEST_CANCELED]: 'Request canceled',
	[CalibrationsTaskStatus.FAILURE]: 'Failure',
	[CalibrationsTaskStatus.PENDING]: 'Pending',
	[CalibrationsTaskStatus.CANCELED]: 'Canceled',
	[CalibrationsTaskStatus.DESYNCHRONIZED]: 'Desynchronized'
};

export function listCalibrationTasks(params: QueryCalibrationTasksListArgs) {
	const { limit, sortDirection, sortBy, page, mgId, status, type } = params;
	const queryParams = buildQueryParams(params, CalibrationTasksSortableColumns);

	return {
		queryKey: ['calibration-tasks', limit, sortDirection, sortBy, page, mgId, status, type],
		queryFn: () => apiClient.get<CalibrationTaskListResponse>(`${CALIBRATION_TASK_ROOT_API_URL}?${queryParams}`)
	};
}

export function revokeTask(taskId: string) {
	return apiClient.delete(`${CALIBRATION_TASK_ROOT_API_URL}/${taskId}`);
}

export function deleteTaskFiles(taskId: string) {
	return apiClient.delete(`${CALIBRATION_TASK_ROOT_API_URL}/${taskId}/files`);
}

export function retryTask(taskId: string) {
	return apiClient.post(`${CALIBRATION_TASK_ROOT_API_URL}/${taskId}/attempts`);
}

export function calibrationTasksListLoader(queryClient: QueryClient) {
	return async ({ request }: { request: Request }) => {
		const url = new URL(request.url);

		const page = url.searchParams.get(Params.page) || '1';
		const limit = url.searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
		const sortBy = url.searchParams.get(Params.sortBy) || '';
		const sortDirection = url.searchParams.get(Params.sortDirection) || '';
		const mgId = url.searchParams.get(Params.mgId) || '';
		const type = url.searchParams.get(Params.type) || '';
		const status = url.searchParams.get(Params.status) || '';

		const queryParams = {
			page,
			limit,
			sortBy,
			sortDirection,
			mgId,
			type,
			status
		};

		return await queryClient.ensureQueryData(listCalibrationTasks(queryParams));
	};
}
